// ============================================================
// xFreeScan by XCloud Solutions - Pricing Page
// 3-column comparison: Free / Pro / Consulting
// ============================================================
import { Link } from 'react-router-dom';
import { usePlan } from '../hooks/usePlan';
import { PlanBadge } from '../components/UpgradePrompt';
import {
  Shield, Zap, Users, Check, X, ArrowRight, Phone,
  Lock, Rocket, Globe, Bug, Radar, Key, Gauge, Code,
  MousePointer, Fingerprint, FileCode, UserSearch
} from 'lucide-react';

const FEATURES = [
  // Category: Escaneo Basico
  { category: 'Escaneo Basico', features: [
    { name: 'Headers de seguridad HTTP', free: true, pro: true },
    { name: 'SSL/TLS y certificados', free: true, pro: true },
    { name: 'DNS, SPF, DKIM, DMARC', free: true, pro: true },
    { name: 'Deteccion de tecnologias', free: true, pro: true },
    { name: 'Escaneo de puertos basico (24)', free: true, pro: true },
    { name: 'Archivos sensibles expuestos', free: true, pro: true },
    { name: 'Subdominios (CT + DNS)', free: true, pro: true },
    { name: 'Paneles de admin expuestos', free: true, pro: true },
    { name: 'Mapeo de CVEs conocidos', free: true, pro: true },
  ]},
  // Category: Escaneo Profundo
  { category: 'Escaneo Profundo', features: [
    { name: 'Nmap (1000 puertos + servicios)', free: false, pro: true },
    { name: 'Nuclei CVE Scanner (templates)', free: false, pro: true },
    { name: 'Nikto Web Server Scanner', free: false, pro: true },
    { name: 'Subfinder + httpx avanzado', free: false, pro: true },
    { name: 'testssl.sh exhaustivo', free: false, pro: true },
  ]},
  // Category: Pentesting Automatizado
  { category: 'Pentesting Automatizado', features: [
    { name: 'Credenciales por defecto (120 pares)', free: false, pro: true },
    { name: 'Test de Rate Limiting (50 intentos)', free: false, pro: true },
    { name: 'CSRF activo (cross-origin test)', free: false, pro: true },
    { name: 'Clickjacking PoC (genera HTML)', free: false, pro: true },
    { name: 'Enumeracion de Tenants', free: false, pro: true },
    { name: 'SQL Injection (16 payloads)', free: false, pro: true },
    { name: 'XSS Reflejado (5 payloads)', free: false, pro: true },
    { name: 'Analisis de tokens de sesion', free: false, pro: true },
    { name: 'Directory Traversal (16 paths)', free: false, pro: true },
    { name: 'Enumeracion de usuarios (timing)', free: false, pro: true },
    { name: 'Analisis de JavaScript (endpoints)', free: false, pro: true },
    { name: 'Info Disclosure avanzada', free: false, pro: true },
  ]},
  // Category: Reportes
  { category: 'Reportes', features: [
    { name: 'Score de seguridad + grade', free: true, pro: true },
    { name: 'Lista de hallazgos con severidad', free: true, pro: true },
    { name: 'Recomendaciones de servicios', free: true, pro: true },
    { name: 'Evidencia verificable (PoC)', free: false, pro: true },
    { name: 'Resumen ejecutivo completo', free: false, pro: true },
    { name: 'Impacto al negocio (costos breach)', free: false, pro: true },
    { name: 'Benchmark vs industria', free: false, pro: true },
    { name: 'Roadmap de remediacion', free: false, pro: true },
    { name: 'Export PDF', free: false, pro: true },
  ]},
  // Category: Limites
  { category: 'Limites', features: [
    { name: 'Escaneos por dia', free: '3', pro: '20' },
    { name: 'Targets maximo', free: '5', pro: '50' },
    { name: 'Historial de escaneos', free: '30 dias', pro: '365 dias' },
    { name: 'Modulos disponibles', free: '9', pro: '26' },
  ]},
];

export default function PricingPage() {
  const { plan } = usePlan();

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Planes xFreeScan</h1>
        <p className="mt-2 text-gray-500 max-w-2xl mx-auto">
          Desde escaneo basico gratuito hasta pentesting automatizado profesional.
          Elige el plan que se ajusta a tu necesidad de seguridad.
        </p>
      </div>

      {/* Plan cards */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* FREE */}
        <div className={`card border-2 ${plan === 'free' ? 'border-purple-400 ring-2 ring-purple-200' : 'border-gray-200'}`}>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
              <Shield className="h-6 w-6 text-gray-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Free</h2>
            <p className="text-3xl font-black text-gray-900 mt-2">$0 <span className="text-sm font-normal text-gray-500">/ siempre</span></p>
            <p className="text-sm text-gray-500 mt-1">Escaneo basico para descubrir riesgos</p>
          </div>
          <div className="space-y-2 mb-6">
            {['9 modulos de escaneo pasivo', 'Paneles admin + CVEs conocidos', 'Reporte basico con score', '3 escaneos/dia', 'Historial 30 dias'].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>
          {plan === 'free' ? (
            <div className="text-center py-2 px-4 bg-gray-100 rounded-lg text-sm font-medium text-gray-600">
              Plan actual
            </div>
          ) : (
            <div className="text-center py-2 px-4 bg-gray-50 rounded-lg text-sm text-gray-400">
              Incluido
            </div>
          )}
        </div>

        {/* PRO */}
        <div className={`card border-2 relative ${plan === 'pro' ? 'border-amber-400 ring-2 ring-amber-200' : 'border-amber-300'} shadow-xl`}>
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold rounded-full shadow-lg">
              <Zap className="h-3 w-3" /> MAS POPULAR
            </span>
          </div>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-3">
              <Rocket className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Pro</h2>
            <p className="text-3xl font-black text-gray-900 mt-2">
              <span className="text-lg font-normal text-gray-500 line-through mr-2">$299</span>
              $149 <span className="text-sm font-normal text-gray-500">USD / mes</span>
            </p>
            <p className="text-sm text-amber-600 font-medium mt-1">Pentesting automatizado profesional</p>
          </div>
          <div className="space-y-2 mb-6">
            {[
              'Todo lo de Free +',
              '5 herramientas deep (Nmap, Nuclei...)',
              '12 modulos de pentesting activo',
              'Brute force, CSRF, XSS, SQLi, clickjacking',
              'Reporte ejecutivo con evidencia PoC',
              'Export PDF para directivos',
              '20 escaneos/dia, 50 targets',
              'Historial 365 dias',
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <Check className={`h-4 w-4 flex-shrink-0 ${i === 0 ? 'text-amber-500' : 'text-green-500'}`} />
                <span className={i === 0 ? 'font-semibold text-amber-700' : ''}>{f}</span>
              </div>
            ))}
          </div>
          {plan === 'pro' ? (
            <div className="text-center py-2.5 px-4 bg-amber-100 rounded-lg text-sm font-medium text-amber-700">
              Plan actual
            </div>
          ) : (
            <a href="mailto:ventas@xclos.com?subject=xFreeScan%20Pro%20-%20Activar%20Plan&body=Hola%2C%20quiero%20activar%20el%20plan%20Pro%20de%20xFreeScan." className="block text-center py-2.5 px-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all">
              <span className="inline-flex items-center gap-1">
                <Zap className="h-4 w-4" /> Activar Pro
              </span>
            </a>
          )}
        </div>

        {/* CONSULTING */}
        <div className="card border-2 border-gray-200">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 mb-3">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Consultoria</h2>
            <p className="text-3xl font-black text-gray-900 mt-2">Custom <span className="text-sm font-normal text-gray-500">/ proyecto</span></p>
            <p className="text-sm text-purple-600 font-medium mt-1">Pentesting manual + remediacion</p>
          </div>
          <div className="space-y-2 mb-6">
            {[
              'Todo lo de Pro +',
              'Pentesting manual por expertos',
              'Cruce con documentacion de infra',
              'Analisis de logica de negocio',
              'Impacto regulatorio (Ley 1581, SIC)',
              'Cadenas de ataque personalizadas',
              'Documento de remediacion (Word/PDF)',
              'Presentacion ejecutiva al C-level',
              'Re-test post-remediacion',
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <Check className={`h-4 w-4 flex-shrink-0 ${i === 0 ? 'text-purple-500' : 'text-green-500'}`} />
                <span className={i === 0 ? 'font-semibold text-purple-700' : ''}>{f}</span>
              </div>
            ))}
          </div>
          <a href="mailto:ventas@xclos.com?subject=xFreeScan%20Consultoria%20-%20Solicitud&body=Hola%2C%20necesito%20un%20pentesting%20manual%20y%20consultoria%20de%20seguridad." className="block text-center py-2.5 px-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all">
            <span className="inline-flex items-center gap-1">
              <Phone className="h-4 w-4" /> Contactar
            </span>
          </a>
        </div>
      </div>

      {/* Feature comparison table */}
      <div className="card overflow-hidden">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Comparacion detallada de funcionalidades</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 w-1/2">Funcionalidad</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500 w-1/6">
                  <div className="flex flex-col items-center gap-1">
                    <Shield className="h-4 w-4 text-gray-400" />
                    Free
                  </div>
                </th>
                <th className="text-center py-3 px-4 text-sm font-medium text-amber-600 w-1/6">
                  <div className="flex flex-col items-center gap-1">
                    <Rocket className="h-4 w-4 text-amber-500" />
                    Pro
                  </div>
                </th>
                <th className="text-center py-3 px-4 text-sm font-medium text-purple-600 w-1/6">
                  <div className="flex flex-col items-center gap-1">
                    <Users className="h-4 w-4 text-purple-500" />
                    Consultoria
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((cat, ci) => (
                <>
                  <tr key={`cat-${ci}`} className="bg-gray-50">
                    <td colSpan={4} className="py-2 px-4 text-sm font-bold text-gray-700">{cat.category}</td>
                  </tr>
                  {cat.features.map((feat, fi) => (
                    <tr key={`feat-${ci}-${fi}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2.5 px-4 text-sm text-gray-700">{feat.name}</td>
                      <td className="text-center py-2.5 px-4">
                        {typeof feat.free === 'boolean' ? (
                          feat.free ? <Check className="h-4 w-4 text-green-500 mx-auto" /> : <X className="h-4 w-4 text-gray-300 mx-auto" />
                        ) : (
                          <span className="text-sm text-gray-600">{feat.free}</span>
                        )}
                      </td>
                      <td className="text-center py-2.5 px-4">
                        {typeof feat.pro === 'boolean' ? (
                          feat.pro ? <Check className="h-4 w-4 text-green-500 mx-auto" /> : <X className="h-4 w-4 text-gray-300 mx-auto" />
                        ) : (
                          <span className="text-sm font-semibold text-amber-700">{feat.pro}</span>
                        )}
                      </td>
                      <td className="text-center py-2.5 px-4">
                        <Check className="h-4 w-4 text-green-500 mx-auto" />
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ / Bottom CTA */}
      <div className="text-center py-8">
        <p className="text-gray-500">
          ¿Preguntas? Contactanos en <a href="mailto:ventas@xclos.com" className="text-purple-600 hover:underline font-medium">ventas@xclos.com</a>
        </p>
        <p className="text-xs text-gray-400 mt-2">
          xFreeScan by XCloud Solutions — Plataforma de diagnostico de ciberseguridad para empresas
        </p>
      </div>
    </div>
  );
}
